/**
 * Built-in note templates and workspace starting templates.
 *
 * Templates insert editable Markdown only. Hints are HTML comments so they are
 * visible in the source editor but never rendered or counted as content.
 * Structured fields (dates, states, people, relations) are NOT written into
 * Markdown — they live in the record panel so there is a single owner.
 */
import type { NoteKind, StartingTemplate, TaskKind, WebPageLook, WebPageTemplate, WorkspaceSection } from "./constants.js";

export interface NoteTemplateDef {
  key: string;
  name: string;
  kind: NoteKind;
  description: string;
  body: string;
}

const hint = (t: string) => `<!-- ${t} -->`;

export const BUILTIN_NOTE_TEMPLATES: NoteTemplateDef[] = [
  {
    key: "reading_note",
    name: "Literature reading note",
    kind: "reading_note",
    description: "Question, method, data, reported findings, limitations, quotations and relevance for one source.",
    body: [
      "## Research question",
      hint("Optional. The question the source addresses, in the authors' terms."),
      "",
      "## Design / method",
      hint("Optional. Study design, method or approach."),
      "",
      "## Data or setting",
      hint("Optional. Data, sample, corpus, site or context."),
      "",
      "## Reported findings",
      hint("What the authors report, with page references, e.g. (p. 4). Keep your own interpretation for the section below."),
      "",
      "## Limitations",
      hint("Optional. Say which limitations the authors state and which you noticed."),
      "",
      "## Quotations and pages",
      hint("Optional. Exact quotations as > blockquotes with page references."),
      "",
      "## My interpretation",
      hint("Optional. Your reading of the source — clearly yours, not the authors'."),
      "",
      "## Relevance to my project",
      hint("Optional. How this source relates to your questions, methods or claims."),
      "",
    ].join("\n"),
  },
  {
    key: "research_question",
    name: "Research question",
    kind: "research_question",
    description: "A question with motivation and scope. A hypothesis is optional.",
    body: [
      "## Question",
      hint("State the question. You do not need a hypothesis to record a question."),
      "",
      "## Motivation",
      hint("Optional. Why the question matters."),
      "",
      "## Scope and constraints",
      hint("Optional."),
      "",
      "## How it could be answered",
      hint("Optional. Candidate methods or evidence."),
      "",
    ].join("\n"),
  },
  {
    key: "hypothesis",
    name: "Hypothesis",
    kind: "hypothesis",
    description: "Statement, rationale, proposed test and assumptions. State changes are recorded in the record panel.",
    body: [
      "## Statement",
      hint("The hypothesis in one or two sentences."),
      "",
      "## Rationale",
      hint("Optional. Why you expect this."),
      "",
      "## Proposed test",
      hint("Optional. What observation would support or contradict it."),
      "",
      "## Assumptions",
      hint("Optional."),
      "",
    ].join("\n"),
  },
  {
    key: "study_plan",
    name: "Study plan",
    kind: "study_plan",
    description: "Method/protocol, sampling, comparisons, planned analysis, evaluation criteria, dependencies and limitations.",
    body: [
      "## Question or hypothesis",
      hint("Link the question or hypothesis in the record panel (Linked records). Summarise it here if useful."),
      "",
      "## Method or protocol",
      hint("Procedure, instruments, interview guide, analysis pipeline, proof strategy…"),
      "",
      "## Sampling or data selection",
      hint("Optional. What is sampled or selected, and why."),
      "",
      "## Controls or comparisons",
      hint("Optional, where relevant: baselines, comparison groups, ablations."),
      "",
      "## Planned analysis",
      hint("Optional."),
      "",
      "## Evaluation criteria",
      hint("Optional. What would count as support, contradiction or an inconclusive outcome."),
      "",
      "## Dependencies",
      hint("Optional. Data access, approvals, equipment, collaborators, tasks."),
      "",
      "## Limitations",
      hint("Optional."),
      "",
    ].join("\n"),
  },
  {
    key: "study_execution",
    name: "Study execution (run)",
    kind: "study_execution",
    description: "Dated observations and deviations for one run of a study plan. The plan version is snapshotted.",
    body: [
      "## Observations",
      hint("Dated observations, one per line, e.g. - 2026-03-04: …"),
      "",
      "## Deviations from the plan",
      hint("What differed from the plan and why. 'None recorded' is a valid entry."),
      "",
      "## Inputs and outputs",
      hint("Describe inputs and outputs. Attach small artifacts or add external links in the record panel."),
      "",
    ].join("\n"),
  },
  {
    key: "result",
    name: "Result",
    kind: "result",
    description: "Observed outcome, artifacts, caveats and interpretation. Negative, null and failed results are valid.",
    body: [
      "## Observed outcome",
      hint("What was observed. Negative, null, contradictory and failed outcomes are valid results."),
      "",
      "## Supporting artifacts",
      hint("Optional. Figures, tables, output files — attach them or add external links in the record panel."),
      "",
      "## Uncertainty and caveats",
      hint("Optional. Your caveats and threats to validity."),
      "",
      "## Interpretation",
      hint("Optional. What you think this means — kept separate from the observed outcome."),
      "",
    ].join("\n"),
  },
  {
    key: "research_log",
    name: "Research log entry",
    kind: "research_log",
    description: "What you did, observed, got stuck on, and what is next.",
    body: [
      "## What I did",
      "",
      "## What I observed",
      hint("Optional."),
      "",
      "## Problems or blockers",
      hint("Optional."),
      "",
      "## Next",
      hint("Optional. Use - [ ] checkboxes; convert them to backlog tasks only if you want to."),
      "",
    ].join("\n"),
  },
  {
    key: "meeting",
    name: "Supervision / team meeting",
    kind: "meeting",
    description: "Agenda, questions, discussion, decisions, blockers and actions. Date and participants live in the record panel.",
    body: [
      "## Agenda",
      "",
      "## Questions",
      hint("Optional. Questions to raise or that were raised."),
      "",
      "## Discussion",
      "",
      "## Decisions",
      hint("Optional. Record important agreed decisions as Decision records too."),
      "",
      "## Blockers",
      hint("Optional."),
      "",
      "## Actions",
      hint('One per line, e.g. - [ ] Draft chapter 2 outline @Name 2026-11-01. Use "Create tasks from actions" to review and add them to the backlog.'),
      "",
    ].join("\n"),
  },
  {
    key: "decision",
    name: "Decision",
    kind: "decision",
    description: "Chosen option, alternatives and rationale. Status (idea vs agreed), date and responsible person are in the record panel.",
    body: [
      "## Chosen option",
      "",
      "## Alternatives considered",
      hint("Optional."),
      "",
      "## Rationale",
      "",
      "## Evidence",
      hint("Link supporting or contradicting evidence with Add evidence link."),
      "",
    ].join("\n"),
  },
  {
    key: "search_log",
    name: "Literature search log",
    kind: "search_log",
    description: "Databases, exact queries, filters, dates, criteria and screening notes. A review record, not a certified protocol.",
    body: [
      hint("A record of your searches. This is a review log, not a claim of systematic-review completeness."),
      "## Inclusion and exclusion criteria",
      "",
      "## Searches",
      "| Search date | Database / source | Exact query | Filters | Notes |",
      "| --- | --- | --- | --- | --- |",
      "|  |  |  |  |  |",
      "",
      "## Screening notes",
      hint("Screening decisions are recorded per library item (review decision + reason)."),
      "",
    ].join("\n"),
  },
  {
    key: "manuscript_section",
    name: "Manuscript section",
    kind: "manuscript_section",
    description: "A section of a manuscript or thesis chapter.",
    body: [
      hint("Write the section here. Insert citations from the library; link claims to evidence. Mark gaps as [PLACEHOLDER: what is missing]."),
      "",
    ].join("\n"),
  },
  {
    key: "progress_note",
    name: "Progress note",
    kind: "progress_note",
    description: "A dated progress note for a selected period.",
    body: ["## Recorded work", "", "## Decisions and results", "", "## Upcoming", "", "## Blockers", ""].join("\n"),
  },
  {
    key: "slides",
    name: "Slides",
    kind: "slides",
    description: "A presentation: slides separated by a line with ---, shown full screen with Present.",
    body: [
      "# Title of the talk",
      "",
      "Your name · date",
      "",
      "---",
      "",
      "## The question",
      "",
      "- One idea per slide",
      "- A few short lines",
      "",
      "Notes: what to say on this slide. Notes show under the slide when you present in the page, not in full screen.",
      "",
      "---",
      "",
      "## What we found",
      "",
      "Write here. Figures, tables, maths and citations work as in notes.",
      "",
      "---",
      "",
      "## Thank you",
      "",
      "Questions?",
      "",
    ].join("\n"),
  },
];

export function builtinTemplate(key: string) {
  return BUILTIN_NOTE_TEMPLATES.find((t) => t.key === key);
}

/** Reading-note sections used by the literature matrix (single storage location: the note's Markdown). */
export const MATRIX_FIELDS = [
  { key: "question", label: "Question", headings: ["Research question", "Question"] },
  { key: "method", label: "Method", headings: ["Design / method", "Method", "Design", "Methods"] },
  { key: "data", label: "Data / setting", headings: ["Data or setting", "Data / setting", "Setting", "Data"] },
  { key: "findings", label: "Reported findings", headings: ["Reported findings", "Key findings", "Findings"] },
  { key: "limitations", label: "Limitations", headings: ["Limitations"] },
  { key: "relevance", label: "Relevance", headings: ["Relevance to my project", "Relevance"] },
] as const;
export type MatrixFieldKey = (typeof MATRIX_FIELDS)[number]["key"];

export interface StartingTemplateItem {
  key: string;
  type: "folder" | "task" | "manuscript";
  label: string;
  description?: string;
  taskKind?: TaskKind;
  manuscriptType?: string;
}

export interface StartingTemplateDef {
  key: StartingTemplate;
  name: string;
  summary: string;
  briefLabel: string;
  items: StartingTemplateItem[];
  suggestedHidden: WorkspaceSection[];
}

const folder = (label: string, description?: string): StartingTemplateItem => ({ key: `folder:${label}`, type: "folder", label, description });
const task = (label: string, taskKind: TaskKind, description: string): StartingTemplateItem => ({
  key: `task:${label}`,
  type: "task",
  label,
  taskKind,
  description,
});

export const STARTING_TEMPLATES: StartingTemplateDef[] = [
  {
    key: "blank",
    name: "Blank project",
    summary: "No folders or tasks. Add structure as you go.",
    briefLabel: "Project brief",
    items: [],
    suggestedHidden: [],
  },
  {
    key: "phd",
    name: "PhD student",
    summary: "Thesis questions, literature, methods, research log, supervision notes, chapter drafts and milestone tasks.",
    briefLabel: "Thesis brief",
    items: [
      folder("Literature", "Reading notes and search logs"),
      folder("Methods", "Study plans and protocols"),
      folder("Research log", "Dated log entries"),
      folder("Supervision", "Supervision meeting notes (personal unless you move them)"),
      folder("Chapters", "Chapter section notes"),
      task("Thesis proposal", "milestone", "Milestone without a date — set your own name and date."),
      task("Progress review", "milestone", "Milestone without a date — set your own name and date."),
      task("Thesis submission", "milestone", "Milestone without a date — set your own name and date."),
      task("Defence preparation", "milestone", "Milestone without a date — rename or delete if it does not apply."),
      { key: "manuscript:Thesis", type: "manuscript", label: "Thesis", manuscriptType: "thesis", description: "Thesis record with an editable review checklist." },
    ],
    suggestedHidden: [],
  },
  {
    key: "postdoc",
    name: "Postdoctoral researcher",
    summary: "Project hypothesis, studies, reproducible results, meetings, manuscripts and deliverable tasks.",
    briefLabel: "Project brief",
    items: [
      folder("Literature", "Reading notes and search logs"),
      folder("Studies", "Study plans and executions"),
      folder("Results", "Result records"),
      folder("Meetings", "Project meeting notes"),
      folder("Manuscripts", "Manuscript section notes"),
      task("Project report", "deliverable", "Deliverable without a date — set your own name and date."),
      task("First manuscript draft", "deliverable", "Deliverable without a date — set your own name and date."),
    ],
    suggestedHidden: [],
  },
  {
    key: "researcher",
    name: "Researcher",
    summary: "Several projects, evidence comparison, documented decisions, collaborators and publication revisions.",
    briefLabel: "Project brief",
    items: [
      folder("Literature", "Reading notes and search logs"),
      folder("Studies", "Study plans, executions and results"),
      folder("Decisions", "Dated project decisions"),
      folder("Meetings", "Meeting notes"),
      folder("Manuscripts", "Manuscript section notes"),
    ],
    suggestedHidden: [],
  },
];

export function startingTemplate(key: string) {
  return STARTING_TEMPLATES.find((t) => t.key === key);
}

/** Parts of a project web page; notes and reports are the ones the person chooses. */
export type WebPagePart = "overview" | "milestones" | "notes" | "manuscripts" | "papers" | "codeData" | "team";

/** The order of pages made before templates (and of "Start from scratch"). */
export const WEB_PAGE_DEFAULT_ORDER: WebPagePart[] = ["overview", "milestones", "notes", "manuscripts", "papers", "codeData", "team"];

export const WEB_PAGE_PART_LABELS: Record<WebPagePart, string> = {
  overview: "Overview of the project",
  milestones: "Milestones and deliverables",
  notes: "Notes you choose",
  manuscripts: "Reports and manuscripts you choose",
  papers: "Papers",
  codeData: "Code and data",
  team: "Team",
};

export interface WebPageTemplateDef {
  key: WebPageTemplate;
  name: string;
  summary: string;
  /** Parts it turns on (for notes and reports: the ones it suggests choosing). */
  parts: WebPagePart[];
  /** Every part, in the order the page shows them. */
  order: WebPagePart[];
  /** A starting introduction the person edits: what the page is for, never facts about the project. */
  intro: string;
  /** The small line above the title. */
  label: string;
  /** Show the date of the latest change under the title. */
  showUpdated: boolean;
  look: WebPageLook;
}

export const WEB_PAGE_TEMPLATES: WebPageTemplateDef[] = [
  {
    key: "summary",
    name: "Project summary",
    summary: "A short presentation of the project: its aims, the papers it builds on, its code and data, and the team.",
    parts: ["overview", "papers", "codeData", "team"],
    order: ["overview", "notes", "manuscripts", "papers", "codeData", "team", "milestones"],
    intro: "A short summary of our project: what we study and why, the papers we build on, our code and data, and the people involved.",
    label: "Project summary",
    showUpdated: false,
    look: "classic",
  },
  {
    key: "funder_update",
    name: "Funder update",
    summary: "A progress report for a funder or partner: aims, milestones and deliverables, the notes you choose and the team.",
    parts: ["overview", "milestones", "notes", "team"],
    order: ["overview", "milestones", "notes", "manuscripts", "team", "papers", "codeData"],
    intro: "This page reports on the progress of the project: its aims, the milestones and deliverables with their current status, and notes on recent work.",
    label: "Progress report",
    showUpdated: true,
    look: "classic",
  },
  {
    key: "results",
    name: "Results",
    summary: "Findings to share: the notes, reports and manuscripts you choose come first, then the papers they cite.",
    parts: ["notes", "manuscripts", "papers"],
    order: ["notes", "manuscripts", "papers", "overview", "milestones", "codeData", "team"],
    intro: "This page shares results of our project. The papers they refer to are listed at the end.",
    label: "Results",
    showUpdated: false,
    look: "minimal",
  },
  {
    key: "website",
    name: "Project website",
    summary: "A public page about the project for anyone interested: its aims, the team, papers, reports, code and data.",
    parts: ["overview", "team", "papers", "manuscripts", "codeData"],
    order: ["overview", "team", "notes", "papers", "manuscripts", "codeData", "milestones"],
    intro: "Welcome to the page of our research project. Here you can read what we study and why, meet the team, and find our papers, reports, code and data.",
    label: "Research project",
    showUpdated: false,
    look: "modern",
  },
  {
    key: "scratch",
    name: "Start from scratch",
    summary: "Nothing chosen yet: pick each part of the page yourself.",
    parts: [],
    order: WEB_PAGE_DEFAULT_ORDER,
    intro: "",
    label: "Research project",
    showUpdated: false,
    look: "classic",
  },
];

export function webPageTemplate(key: string | null | undefined) {
  return WEB_PAGE_TEMPLATES.find((t) => t.key === key);
}

export const WEB_PAGE_LOOKS: { key: WebPageLook; name: string; summary: string }[] = [
  { key: "classic", name: "Classic", summary: "Serif headings on warm paper, like a printed report." },
  { key: "modern", name: "Modern", summary: "Clean lettering under a coloured band, like a website." },
  { key: "minimal", name: "Minimal", summary: "Plain text on white, with nothing extra." },
];

export const THESIS_CHECKLIST_DEFAULT = [
  { id: "c1", label: "Chapter drafts shared for internal review", done: false, date: null as string | null },
  { id: "c2", label: "Supervisor feedback addressed", done: false, date: null as string | null },
  { id: "c3", label: "Examination / defence milestone dates confirmed (your institution's rules)", done: false, date: null as string | null },
];

export interface ProjectBriefFields {
  questions: { text: string; recordId?: string | null }[];
  motivation: string;
  contribution: string;
  scope: string;
  constraints: string;
  targetOutputs: string;
  successCriteria: string;
  /** The project proposal, kept as a note ("Set up from proposal"); used as context for the assistant. */
  proposalNoteId?: string | null;
}

export const EMPTY_BRIEF: ProjectBriefFields = {
  questions: [],
  motivation: "",
  contribution: "",
  scope: "",
  constraints: "",
  targetOutputs: "",
  successCriteria: "",
};

export const BRIEF_FIELD_LABELS: Record<Exclude<keyof ProjectBriefFields, "questions" | "proposalNoteId">, string> = {
  motivation: "Motivation",
  contribution: "Intended contribution",
  scope: "Scope",
  constraints: "Constraints",
  targetOutputs: "Target outputs",
  successCriteria: "Success or evaluation criteria",
};
